import React, { useRef, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeOff } from 'lucide-react';
import { useAbielAudio } from './AbielAudioContext';

interface GlobalAudioPlayerControlProps {
  controlType?: 'play-pause' | 'mute' | 'volume-show' | 'volume-horizontal' | 'icon-only' | 'indicator-only' | 'full';
  variant?: 'header' | 'action-button' | 'floating-player' | 'golden';
  className?: string;
  collapsible?: boolean;
  isScrolled?: boolean;
}

export default function GlobalAudioPlayerControl({
  controlType = 'mute',
  variant = 'header',
  className = '',
  collapsible = true,
  isScrolled = false
}: GlobalAudioPlayerControlProps) {
  const { 
    isPlaying, 
    pause, 
    currentSessionId, 
    isMuted, 
    toggleMute, 
    volume, 
    setVolume 
  } = useAbielAudio();

  const volumeBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  console.log('🎵 GlobalAudioPlayerControl render:', { 
    controlType, 
    variant, 
    isPlaying, 
    currentSessionId, 
    isMuted, 
    volume,
    isScrolled 
  });

  // Handle play/pause functionality
  const handlePlayPause = () => {
    console.log('🎵 GlobalAudioPlayerControl play/pause clicked:', { isPlaying, currentSessionId });
    if (isPlaying) {
      pause();
    }
    // Note: Play functionality should be handled by parent component with specific prayer context
  };

  // Handle mute/unmute functionality
  const handleMute = () => {
    console.log('🎵 GlobalAudioPlayerControl mute toggle clicked:', { isMuted, volume });
    toggleMute();
  };

  // Calculate volume from position
  const calculateVolumeFromPosition = useCallback((clientX: number) => {
    if (!volumeBarRef.current) return volume;
    
    const rect = volumeBarRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    
    // Snap to common volume levels for better UX
    if (percentage < 0.05) return 0;
    if (percentage < 0.125) return 0.25;
    if (percentage < 0.375) return 0.5;
    if (percentage < 0.625) return 0.75;
    if (percentage < 0.875) return 1.0;
    return percentage;
  }, [volume]);

  // Handle volume bar click/touch start
  const handleVolumeStart = useCallback((clientX: number) => {
    if (isMuted) {
      // If muted, unmute first
      toggleMute();
      return;
    }
    
    const newVolume = calculateVolumeFromPosition(clientX);
    console.log('🎵 Volume interaction started:', { newVolume, clientX });
    setVolume(newVolume);
    setIsDragging(true);
  }, [isMuted, toggleMute, calculateVolumeFromPosition, setVolume]);

  // Handle volume bar mouse events
  const handleVolumeMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleVolumeStart(event.clientX);
  }, [handleVolumeStart]);

  const handleVolumeMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) return;
    
    const newVolume = calculateVolumeFromPosition(event.clientX);
    setVolume(newVolume);
  }, [isDragging, calculateVolumeFromPosition, setVolume]);

  const handleVolumeMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle volume bar touch events
  const handleVolumeTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault();
    const touch = event.touches[0];
    handleVolumeStart(touch.clientX);
  }, [handleVolumeStart]);

  const handleVolumeTouchMove = useCallback((event: TouchEvent) => {
    if (!isDragging) return;
    event.preventDefault();
    
    const touch = event.touches[0];
    const newVolume = calculateVolumeFromPosition(touch.clientX);
    setVolume(newVolume);
  }, [isDragging, calculateVolumeFromPosition, setVolume]);

  const handleVolumeTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Set up global event listeners for drag operations
  React.useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleVolumeMouseMove(e);
      const handleMouseUp = () => handleVolumeMouseUp();
      const handleTouchMove = (e: TouchEvent) => handleVolumeTouchMove(e);
      const handleTouchEnd = () => handleVolumeTouchEnd();

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleVolumeMouseMove, handleVolumeMouseUp, handleVolumeTouchMove, handleVolumeTouchEnd]);

  // Render horizontal volume bar component (compact for header)
  const renderHorizontalVolumeBar = () => {
    return (
      <div className="w-8 h-2">
        <div 
          ref={volumeBarRef}
          className={`h-2 bg-white/20 dark:bg-black/30 rounded-full cursor-pointer hover:bg-white/30 dark:hover:bg-black/40 transition-all duration-200 relative select-none ${
            isDragging ? 'bg-white/40 dark:bg-black/50 scale-y-125' : ''
          }`}
          onMouseDown={handleVolumeMouseDown}
          onTouchStart={handleVolumeTouchStart}
        >
          <div 
            className="h-2 bg-[#F6C948] rounded-full transition-all duration-200 pointer-events-none"
            style={{ width: `${isMuted ? 0 : volume * 100}%` }}
          />
          
          {/* Volume thumb indicator - smaller for horizontal layout */}
          {!isMuted && (
            <div 
              className={`absolute top-1/2 w-2 h-2 bg-[#F6C948] border border-white dark:border-black rounded-full transform -translate-y-1/2 transition-all duration-200 ${
                isDragging ? 'scale-125 shadow-lg' : 'scale-100'
              }`}
              style={{ left: `calc(${volume * 100}% - 4px)` }}
            />
          )}
        </div>
      </div>
    );
  };

  // Render interactive volume bar component (original vertical layout)
  const renderVolumeBar = () => {
    if (controlType !== 'volume-show') return null;
    
    return (
      <div className="mt-1 w-full max-w-[32px]">
        <div 
          ref={volumeBarRef}
          className={`h-2 bg-white/20 dark:bg-black/30 rounded-full cursor-pointer hover:bg-white/30 dark:hover:bg-black/40 transition-all duration-200 relative select-none ${
            isDragging ? 'bg-white/40 dark:bg-black/50 scale-y-125' : ''
          }`}
          onMouseDown={handleVolumeMouseDown}
          onTouchStart={handleVolumeTouchStart}
        >
          <div 
            className="h-2 bg-[#F6C948] rounded-full transition-all duration-200 pointer-events-none"
            style={{ width: `${isMuted ? 0 : volume * 100}%` }}
          />
          
          {/* Volume thumb indicator */}
          {!isMuted && (
            <div 
              className={`absolute top-1/2 w-3 h-3 bg-[#F6C948] border-2 border-white dark:border-black rounded-full transform -translate-y-1/2 transition-all duration-200 ${
                isDragging ? 'scale-125 shadow-lg' : 'scale-100'
              }`}
              style={{ left: `calc(${volume * 100}% - 6px)` }}
            />
          )}
        </div>
        
        {/* Volume level indicator */}
        <div className="mt-1 text-[10px] text-center text-gray-500 dark:text-gray-400">
          {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
        </div>
      </div>
    );
  };

  // Control type: icon-only (just the volume icon)
  if (controlType === 'icon-only') {
    return (
      <button
        onClick={handleMute}
        disabled={!currentSessionId}
        className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors ${
          !currentSessionId ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
        aria-label={isMuted ? "Unmute voice" : "Mute voice"}
      >
        {isMuted ? (
          <VolumeOff size={16} className={`transition-colors duration-300 ${
            isScrolled 
              ? 'text-white hover:text-[#F6C948]' 
              : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-gray-400'
          }`} />
        ) : (
          <Volume2 size={16} className={`transition-colors duration-300 ${
            isScrolled 
              ? 'text-white hover:text-[#F6C948]' 
              : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
          }`} />
        )}
      </button>
    );
  }

  // Control type: indicator-only (just the volume bar and percentage)
  if (controlType === 'indicator-only') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {renderHorizontalVolumeBar()}
        <span className="text-[10px] text-[#6E6E6E] dark:text-gray-400 min-w-[24px] text-center">
          {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
        </span>
      </div>
    );
  }

  // Control type: play-pause
  if (controlType === 'play-pause') {
    return (
      <button
        onClick={handlePlayPause}
        className={`h-8 w-8 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
          isPlaying
            ? "bg-[#F6C948]/20 border-[#F6C948]/40 text-[#F6C948]"
            : "border-gray-300 text-gray-600 hover:bg-[#F6C948]/10 hover:text-[#F6C948]"
        } ${className}`}
        aria-label={isPlaying ? "Pause voice" : "Listen to prayer"}
      >
        {isPlaying ? (
          <Pause size={14} />
        ) : (
          <Play size={14} className="ml-0.5" />
        )}
      </button>
    );
  }

  // Control type: volume-horizontal (new compact horizontal layout)
  if (controlType === 'volume-horizontal') {
    return (
      <div className={`flex items-center gap-2 h-6 ${className}`}>
        <button
          onClick={handleMute}
          disabled={!currentSessionId}
          className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors flex-shrink-0 ${
            !currentSessionId ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          aria-label={isMuted ? "Unmute voice" : "Mute voice"}
        >
          {isMuted ? (
            <VolumeOff size={16} className={`transition-colors duration-300 ${
              isScrolled 
                ? 'text-white hover:text-[#F6C948]' 
                : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-gray-400'
            }`} />
          ) : (
            <Volume2 size={16} className={`transition-colors duration-300 ${
              isScrolled 
                ? 'text-white hover:text-[#F6C948]' 
                : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
            }`} />
          )}
        </button>
        {renderHorizontalVolumeBar()}
        <span className="text-[10px] text-[#6E6E6E] dark:text-gray-400 min-w-[24px] text-center">
          {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
        </span>
      </div>
    );
  }

  // Control type: volume-show (mute/unmute with interactive volume bar)
  if (controlType === 'volume-show') {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        <button
          onClick={handleMute}
          disabled={!currentSessionId}
          className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors ${
            !currentSessionId ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          aria-label={isMuted ? "Unmute voice" : "Mute voice"}
        >
          {isMuted ? (
            <VolumeOff size={16} className="text-gray-500 dark:text-gray-400" />
          ) : (
            <Volume2 size={16} className={`transition-colors duration-300 ${
              isScrolled 
                ? 'text-white hover:text-[#F6C948]' 
                : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
            }`} />
          )}
        </button>
        {renderVolumeBar()}
      </div>
    );
  }

  // Control type: mute (legacy compatibility)
  if (controlType === 'mute') {
    return (
      <button
        onClick={handleMute}
        disabled={!currentSessionId}
        className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors ${
          !currentSessionId ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
        aria-label={isMuted ? "Unmute voice" : "Mute voice"}
      >
        {isMuted ? (
          <VolumeOff size={16} className="text-gray-500 dark:text-gray-400" />
        ) : (
          <Volume2 size={16} className={`transition-colors duration-300 ${
            isScrolled 
              ? 'text-white hover:text-[#F6C948]' 
              : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
          }`} />
        )}
      </button>
    );
  }

  // Control type: full (both play/pause and volume controls)
  if (controlType === 'full') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={handlePlayPause}
          className="h-8 w-8 rounded-full border-2 transition-all duration-300 flex items-center justify-center bg-[#F6C948]/20 border-[#F6C948]/40 text-[#F6C948]"
          aria-label={isPlaying ? "Pause voice" : "Listen to prayer"}
        >
          {isPlaying ? (
            <Pause size={14} />
          ) : (
            <Play size={14} className="ml-0.5" />
          )}
        </button>
        
        <div className="flex flex-col items-center">
          <button
            onClick={handleMute}
            disabled={!currentSessionId}
            className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors ${
              !currentSessionId ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            aria-label={isMuted ? "Unmute voice" : "Mute voice"}
          >
            {isMuted ? (
              <VolumeOff size={16} className="text-gray-500 dark:text-gray-400" />
            ) : (
              <Volume2 size={16} className={`transition-colors duration-300 ${
                isScrolled 
                  ? 'text-white hover:text-[#F6C948]' 
                  : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
              }`} />
            )}
          </button>
          {renderVolumeBar()}
        </div>
      </div>
    );
  }

  // Default: header variant for backward compatibility
  return (
    <button
      onClick={handleMute}
      className={`h-6 w-6 p-0 rounded-lg hover:bg-white/10 dark:hover:bg-black/20 transition-colors ${className}`}
      aria-label={isPlaying ? "Prayer voice controls" : "No voice playing"}
    >
      {isPlaying ? (
        <Volume2 size={16} className={`transition-colors duration-300 ${
          isScrolled 
            ? 'text-white hover:text-[#F6C948]' 
            : 'text-[#6E6E6E] hover:text-[#F6C948] dark:text-[#F6C948]'
        }`} />
      ) : (
        <VolumeOff size={16} className="text-gray-500 dark:text-gray-400" />
      )}
    </button>
  );
}
